<script>
import UebungenLibraryCuratedCategoryTile from "./UebungenLibraryCuratedCategoryTile.vue";

export default {
  name: "uebungen-library-curated-dashboard",
  components: {
    UebungenLibraryCuratedCategoryTile
  },
  props: {
    uebungen: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    groupedUebungen() {
      const grouped = {};

      this.uebungen.forEach(uebung => {
        const resolvedUebung = this.$resolveUebungFromRegistry(uebung.uebung_type);
        if (!resolvedUebung) return;

        if (resolvedUebung.options?.is_curatable === false) return;

        const categoryAlias = resolvedUebung.category;
        const moduleAlias = resolvedUebung.alias;

        if (!grouped[categoryAlias]) {
          const categoryData = this.$UebungenRegistry?.registered_categories?.[categoryAlias];
          if (!categoryData) return;

          grouped[categoryAlias] = {
            category: categoryData,
            modules: {}
          };
        }

        if (!grouped[categoryAlias].modules[moduleAlias]) {
          grouped[categoryAlias].modules[moduleAlias] = {
            alias: moduleAlias,
            uebungen: []
          };
        }

        grouped[categoryAlias].modules[moduleAlias].uebungen.push(uebung);
      });

      return grouped;
    },

    orderedCategories() {
      const categories = Object.entries(this.groupedUebungen);

      return categories.sort((a, b) => {
        const countA = Object.values(a[1].modules).reduce((sum, m) => sum + m.uebungen.length, 0);
        const countB = Object.values(b[1].modules).reduce((sum, m) => sum + m.uebungen.length, 0);
        return countB - countA;
      });
    },

    hasUebungen() {
      return this.orderedCategories.length > 0;
    }
  },
  methods: {
    getAnimationDelay(index) {
      return `animation-delay: ${(index + 1) * 100}ms`;
    },
    getCategoryGridClass(index) {
      return index === 0
        ? 'curated-dashboard__grid-item--primary'
        : 'curated-dashboard__grid-item--secondary';
    }
  }
}
</script>

<template>
  <div class="curated-dashboard p-6">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <loading-comet></loading-comet>
    </div>

    <!-- Empty State -->
    <div v-else-if="!hasUebungen" class="flex flex-col items-center justify-center py-20">
      <div class="w-24 h-24 bg-grey-200 rounded-full flex items-center justify-center mb-6">
        <i class="fas fa-folder-open text-4xl text-grey-400"></i>
      </div>
      <h3 class="text-2xl font-semibold text-grey-600 mb-2">Keine kuratierten Übungen</h3>
      <p class="text-grey-500 text-center max-w-md">
        Es sind aktuell keine kuratierten Übungen verfügbar.
      </p>
    </div>

    <!-- Dashboard Grid -->
    <div v-else class="curated-dashboard__grid">
      <div
        v-for="([categoryAlias, categoryData], index) in orderedCategories"
        :key="categoryAlias"
        :class="getCategoryGridClass(index)"
        class="animate-fade-slide"
        :style="getAnimationDelay(index)">
        <UebungenLibraryCuratedCategoryTile
          :category="categoryData.category"
          :category-alias="categoryAlias"
          :uebungen-by-module="categoryData.modules"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.curated-dashboard__grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 3fr 2fr;
}

.curated-dashboard__grid-item--primary {
  grid-row: 1 / -1;
}

/* Tablet: zwei gleiche Spalten */
@media (max-width: 1024px) {
  .curated-dashboard__grid {
    grid-template-columns: 1fr 1fr;
  }

  .curated-dashboard__grid-item--primary {
    grid-row: auto;
  }
}

/* Mobile: eine Spalte */
@media (max-width: 768px) {
  .curated-dashboard__grid {
    grid-template-columns: 1fr;
  }
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-slide {
  animation: fadeSlideIn 0.5s ease-out forwards;
  opacity: 0;
}
</style>
